import { Outlet, useParams } from "@tanstack/react-router";

import { useStore } from "@/store";
import { useEffect } from "react";

/**
 * CompanyLayout component that wraps all company-specific routes.
 * Includes the Sidebar and handles company context.
 */
export function CompanyLayout() {
  const params = useParams({ strict: false });
  const companyId = params.company_id;
  const isLoading = useStore((state) => state.isLoading);
  const error = useStore((state) => state.error);
  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const getCompanyById = useStore((state) => state.getCompanyById);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    // Fetch companies if not already loaded
    void fetchCompanies({ force: false });
  }, [companyId, fetchCompanies]);

  // Get company from store
  const company = companyId ? getCompanyById(companyId) : null;

  if (!companyId) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          Company ID is missing
        </div>
      </div>
    );
  }

  // Only block render when we have no company data at all — a background re-fetch
  // should not flash the loading state if we already have a cached company.
  if (isLoading && !company) {
    return (
      <div className="flex h-full flex-col gap-4 p-6">
        <div className="h-6 w-48 animate-pulse rounded-md bg-muted" />
        <div className="h-4 w-72 animate-pulse rounded-md bg-muted" />
        <div className="mt-4 flex gap-4">
          <div className="h-32 flex-1 animate-pulse rounded-xl bg-muted" />
          <div className="h-32 flex-1 animate-pulse rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error ?? "Company not found or you don't have access to it."}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <Outlet />
    </div>
  );
}
